import { useMemo, useState } from 'react';

import { Button, Grid, Typography } from '@mui/material';
import { AddResourcePermissionType, ResourceType } from '@prisma/client';
import { GetPartnerResources, Resource } from 'types/graphql';

import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import { ModalBox } from 'src/components/ModalContent/Modal';
import ResourceForm from 'src/components/ResourceForm';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { RESOURCES_TABLE_HEADERS } from 'src/constants';
import { ADD_PARTNER_RESOURCES } from 'src/graphql/mutations/addPartnerResources';
import { DELETE_PARTNER_RESOURCES } from 'src/graphql/mutations/deletePartnerResources.mutation';
import {
    GET_ALL_PARTNER_RESOURCES_QUERY,
    GET_PARTNER_RESOURCES_QUERY,
} from 'src/graphql/queries/partnerResources.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { LogError } from 'src/lib';
import {
    filteredManageResourceTableData,
    managePartnerResourcesFilterOptions,
    updateTableFilters,
} from 'src/utils/tableUtils';
import { getEnumDropDownOptions } from 'src/utils/utils';
import { partnerResourceValidationSchema } from 'src/utils/validations';

import { ViewResourceModal } from '../ViewPartnerResources/components';

const ManagePartnerResourcesPage = () => {
    const [tableFilters, setTableFilters] = useState({});
    const [showAddResourceModal, setShowAddResourceModal] = useState(false);
    const [showEditResourceModal, setShowEditResourceModal] = useState(false);
    const [openResourceModal, setOpenResourceModal] = useState(false);
    const [selectedResource, setSelectedResource] = useState(null);
    const [showDeleteResourceModal, setShowDeleteResourceModal] = useState({
        open: false,
        id: null,
    });

    const {
        data,
        loading,
        refetch: fetchLatestResources,
    } = useQuery(GET_ALL_PARTNER_RESOURCES_QUERY, {
        fetchPolicy: 'cache-and-network',
        onError: (err) => {
            return showErrorToast(err.message);
        },
    });

    const filterOptions = () => {
        return managePartnerResourcesFilterOptions(data?.getAllPartnerResources);
    };

    const { data: resourceSections, refetch: refetchLatestResources } =
        useQuery<GetPartnerResources>(GET_PARTNER_RESOURCES_QUERY, {
            fetchPolicy: 'cache-and-network',
            onError: (error) => {
                showErrorToast('There was an error retrieving the resources');
                LogError(error);
            },
        });

    const [createResourceMutation] = useMutation(ADD_PARTNER_RESOURCES, {
        onCompleted: () => {
            showSuccessToast('Resource Added!');
            setShowAddResourceModal(false);
            fetchLatestResources();
        },
        onError: () => {
            showErrorToast('Error Creating Partner Resource');
        },
    });

    const [deleteResourceMutation] = useMutation(DELETE_PARTNER_RESOURCES, {
        onCompleted: () => {
            fetchLatestResources();
            showSuccessToast('Resource Deleted!');
            setShowDeleteResourceModal({ open: false, id: null });
        },
        onError: () => {
            showErrorToast('Error Deleting Partner Resource');
        },
    });

    const partnerResourceSections = resourceSections?.getPartnerResources || [];

    const tableData = useMemo(() => {
        return (
            filteredManageResourceTableData(data?.getAllPartnerResources, tableFilters)?.map(
                (resource) => {
                    return {
                        ...resource,
                        status: <StatusChip status={resource.status} type="partnerResource" />,
                    };
                }
            ) ?? []
        );
    }, [tableFilters, data]);

    const handleCloseViewModal = () => {
        setOpenResourceModal(false);
    };

    const updatePartnerResourceAuditLog = (id: number) => {
        refetchLatestResources({
            id,
        });
    };

    const handleOpenViewResourceModal = (value: Resource) => {
        updatePartnerResourceAuditLog(value.id);
        setSelectedResource(value);
        setOpenResourceModal(true);
    };

    const addNewResource = () => {
        setShowAddResourceModal(true);
    };

    const handleEditResourceModal = () => {
        setShowEditResourceModal(!showEditResourceModal);
    };

    const getSectionDropDownOptions = () => {
        return (
            partnerResourceSections?.map((value) => {
                return {
                    label: value.name.replace(/_/g, ' '),
                    value: value.id,
                };
            }) ?? []
        );
    };

    return (
        <PageContent
            heading="Manage Partner Resources"
            subHeading="Add, View, Remove and Update resources as necessary"
        >
            <MetaTags
                title="Manage Partner Resources"
                description="Manage partner resources page"
            />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {loading || !data ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Resource"
                            data={tableData}
                            headers={RESOURCES_TABLE_HEADERS}
                            title="Resource List"
                            addRecordCallBack={addNewResource}
                            handleEditRecord={(input) => {
                                setSelectedResource(input);
                                setShowEditResourceModal(true);
                            }}
                            handleViewRecord={handleOpenViewResourceModal}
                            deleteRecordCallBack={(id) => {
                                setShowDeleteResourceModal({ open: true, id });
                            }}
                            enableFilter
                            enableExport
                            enableEdit
                            emptyRowColSpan={5}
                            filterOptions={filterOptions()}
                            updateTableFilters={(filter: string, newFilterValue: string) => {
                                return setTableFilters((prevFilters) => {
                                    return updateTableFilters(prevFilters, newFilterValue, filter);
                                });
                            }}
                        />
                    )}
                </Grid>
            </Grid>
            <ModalBox
                handleCloseModal={() => {
                    return setShowAddResourceModal(false);
                }}
                open={showAddResourceModal}
                widthSize="800px"
                title="Add New Resource"
            >
                <ResourceForm
                    isEditMode={false}
                    initialValues={{
                        resource_name: '',
                        description: '',
                        viewAccessPermissions: AddResourcePermissionType.ALL_PARTNERS,
                        resource_type: '',
                        resourceURL: '',
                        imageUrl: '',
                        section: null,
                    }}
                    sectionsOptions={getSectionDropDownOptions()}
                    resourceTypeOptions={getEnumDropDownOptions(ResourceType)}
                    onCancel={() => {
                        return setShowAddResourceModal(false);
                    }}
                    onSubmit={async (formik) => {
                        createResourceMutation({
                            variables: {
                                input: {
                                    name: formik.resource_name,
                                    description: formik.description,
                                    type: formik.resource_type,
                                    imageUrl: formik.imageUrl,
                                    resourceUrl: formik.resourceURL,
                                    sectionId: formik.section,
                                    status: 'ACTIVE',
                                    permission: formik.viewAccessPermissions,
                                },
                            },
                        });
                    }}
                    validationSchema={partnerResourceValidationSchema}
                />
            </ModalBox>

            <ModalBox
                handleCloseModal={() => {
                    return setShowDeleteResourceModal({
                        open: false,
                        id: null,
                    });
                }}
                open={showDeleteResourceModal.open}
                widthSize="500px"
                title="Delete Resource?"
            >
                <Flex gap={4} flexDirection="column" padding="16px 8px">
                    <Typography sx={{ fontSize: '16px' }} pt={2}>
                        Are you sure you want to delete Resource?
                    </Typography>

                    <Flex gap="24px" justifyContent={'center'}>
                        <Button
                            onClick={() => {
                                return setShowDeleteResourceModal({
                                    open: false,
                                    id: null,
                                });
                            }}
                            variant="main"
                        >
                            Cancel
                        </Button>

                        <Button
                            onClick={(event) => {
                                event.preventDefault();
                                deleteResourceMutation({
                                    variables: { input: { id: showDeleteResourceModal.id } },
                                });
                            }}
                            variant="main"
                        >
                            Confirm
                        </Button>
                    </Flex>
                </Flex>
            </ModalBox>
            <ViewResourceModal
                open={openResourceModal}
                handleCloseModal={handleCloseViewModal}
                resource={selectedResource}
                partnerResourceSections={partnerResourceSections}
                refetchLatestResources={fetchLatestResources}
                handleEditResourceModal={handleEditResourceModal}
                showEditResourceModal={showEditResourceModal}
                showEditIcon
            />
        </PageContent>
    );
};

export default ManagePartnerResourcesPage;
